import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import DriverModal from '../components/drivers/DriverModal';
import DeleteConfirmModal from '../components/drivers/DeleteConfirmModal';

const Drivers = () => {
    const { isDark } = useTheme();
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingDriver, setEditingDriver] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Mobile check
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchDrivers = useCallback(async () => {
        try {
            const response = await fetch('/api/v1/drivers', {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            const data = await response.json();
            setDrivers(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching drivers:', error);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDrivers();
    }, [fetchDrivers]);

    const handleDelete = async (id) => {
        try {
            await fetch(`/api/v1/drivers/${id}`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            setDrivers(drivers.filter(driver => driver.id !== id));
            setDeleteConfirm(null);
        } catch (error) {
            console.error('Error deleting driver:', error);
        }
    };

    const handleSave = (savedDriver) => {
        if (editingDriver) {
            setDrivers(drivers.map(driver =>
                driver.id === savedDriver.id ? { ...driver, ...savedDriver } : driver
            ));
        } else {
            setDrivers([...drivers, savedDriver]);
        }
        setShowModal(false);
        setEditingDriver(null);
        fetchDrivers(); // Refresh to get calculated days
    };

    const filteredDrivers = drivers.filter(driver =>
        `${driver.first_name} ${driver.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        driver.phone_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        driver.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const styles = {
        container: {
            padding: '1rem 0',
            background: isDark ? '#0a0a0a' : '#f8f9fa',
            minHeight: '100vh',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            marginBottom: '2rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '1rem' : '0',
        },
        title: {
            fontSize: '2rem',
            fontWeight: 'bold',
            color: isDark ? '#ffffff' : '#1f2937',
            marginBottom: isMobile ? '0.5rem' : '0',
        },
        count: {
            fontSize: '1.25rem',
            color: isDark ? '#9ca3af' : '#6b7280',
            marginLeft: '0.5rem',
        },
        searchContainer: {
            flex: 1,
            maxWidth: isMobile ? '100%' : '400px',
            marginRight: isMobile ? '0' : '1rem',
            marginBottom: isMobile ? '0.5rem' : '0',
        },
        searchInput: {
            width: '100%',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#1f2937' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
        },
        addButton: {
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
            transition: '0.2s',
            width: isMobile ? '100%' : 'auto',
        },
        tableContainer: {
            background: isMobile ? 'transparent' : (isDark ? '#1e1e1e' : '#ffffff'),
            borderRadius: '1rem',
            marginLeft: isMobile ? '0' : '1rem',
            marginRight: isMobile ? '0' : '1rem',
            padding: isMobile ? '0' : '0',
            boxShadow: isMobile ? 'none' : (isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'),
            overflowX: 'auto',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
        },
        th: {
            textAlign: 'left',
            padding: '1rem',
            borderBottom: `2px solid ${isDark ? '#333' : '#e5e7eb'}`,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isDark ? '#9ca3af' : '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
        },
        td: {
            padding: '1rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            color: isDark ? '#d1d5db' : '#374151',
        },
        link: {
            color: isDark ? '#8b5cf6' : '#6d28d9',
            textDecoration: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            marginRight: '0.5rem',
        },
        actionButton: {
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.75rem',
            fontWeight: 600,
            marginRight: '0.5rem',
            transition: '0.2s',
        },
        editButton: {
            background: isDark ? '#1e40af' : '#3b82f6',
            color: 'white',
        },
        deleteButton: {
            background: isDark ? '#991b1b' : '#ef4444',
            color: 'white',
        },
        emptyState: {
            textAlign: 'center',
            padding: '4rem 2rem',
            color: isDark ? '#9ca3af' : '#6b7280',
        },
        // Mobile Card Styles
        card: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            padding: '1.25rem',
            marginBottom: '1rem',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            margin: '0 1rem 1rem 1rem',
        },
        cardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            paddingBottom: '0.75rem',
        },
        cardRow: {
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
        },
        cardLabel: {
            color: isDark ? '#9ca3af' : '#6b7280',
            fontWeight: 500,
        },
        cardValue: {
            color: isDark ? '#d1d5db' : '#374151',
            fontWeight: 600,
            textAlign: 'right',
        },
        // Grid Action Links for Mobile
        gridActions: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.5rem',
            marginTop: '1rem',
            marginBottom: '1rem',
        },
        gridActionLink: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.75rem',
            background: isDark ? '#374151' : '#f3f4f6',
            borderRadius: '0.5rem',
            color: isDark ? '#d1d5db' : '#374151',
            textDecoration: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            transition: 'background 0.2s',
        }
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={{ ...styles.emptyState, minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div>Loading drivers...</div>
                </div>
            </div>
        );
    }

    const renderMobileCard = (driver) => (
        <div key={driver.id} style={styles.card}>
            <div style={styles.cardHeader}>
                <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: isDark ? '#fff' : '#1f2937' }}>
                    {driver.first_name} {driver.last_name}
                </div>
                <span style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: driver.status === 'Active' ? '#dcfce7' : (isDark ? '#374151' : '#e5e7eb'),
                    color: driver.status === 'Active' ? '#166534' : (isDark ? '#d1d5db' : '#374151'),
                }}>
                    {driver.status}
                </span>
            </div>

            <div style={styles.cardRow}>
                <span style={styles.cardLabel}>Car</span>
                <span style={{ ...styles.cardValue, fontWeight: 'bold' }}>
                    {driver.car_color || 'No Car Assigned'}
                </span>
            </div>

            <div style={styles.cardRow}>
                <span style={styles.cardLabel}>Phone</span>
                <span style={styles.cardValue}>{driver.phone_number}</span>
            </div>

            <div style={styles.cardRow}>
                <span style={styles.cardLabel}>Days Done</span>
                <span style={{ ...styles.cardValue, color: '#8b5cf6' }}>
                    {driver.days_done} Days
                </span>
            </div>

            {/* Quick Actions Grid */}
            <div style={styles.gridActions}>
                <a href={`/home/driver_graph?driver=${driver.id}`} style={styles.gridActionLink}>
                    📊 Graph
                </a>
                <a href={`/sales/driver_specific_sales?driver=${driver.id}`} style={styles.gridActionLink}>
                    💰 Sales
                </a>
                <a href={`/sales/driver_specific_expenses?driver=${driver.id}`} style={styles.gridActionLink}>
                    💸 Expenses
                </a>
                <a href={`/sales/driver_specific_sales_this_month?driver=${driver.id}`} style={styles.gridActionLink}>
                    📅 This Month
                </a>
            </div>

            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                <button
                    onClick={() => {
                        setEditingDriver(driver);
                        setShowModal(true);
                    }}
                    style={{ ...styles.actionButton, ...styles.editButton, flex: 1, textAlign: 'center', marginRight: 0 }}
                >
                    Edit
                </button>
                <button
                    onClick={() => setDeleteConfirm(driver)}
                    style={{ ...styles.actionButton, ...styles.deleteButton, flex: 1, textAlign: 'center', marginRight: 0 }}
                >
                    Delete
                </button>
            </div>
        </div>
    );

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: isMobile ? '0.5rem' : '0', width: isMobile ? '100%' : 'auto' }}>
                    <h1 style={styles.title}>Drivers</h1>
                    <span style={styles.count}>[{filteredDrivers.length}]</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end', width: isMobile ? '100%' : 'auto', flexDirection: isMobile ? 'column' : 'row' }}>
                    <div style={styles.searchContainer}>
                        <input
                            type="text"
                            placeholder="Search drivers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                    <button
                        onClick={() => {
                            setEditingDriver(null);
                            setShowModal(true);
                        }}
                        style={styles.addButton}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                        + Add New Driver
                    </button>
                </div>
            </div>

            <div style={styles.tableContainer}>
                {filteredDrivers.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👨‍✈️</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                            {searchTerm ? 'No drivers found' : 'No drivers yet'}
                        </div>
                        <div>
                            {searchTerm ? 'Try a different search term' : 'Click "Add New Driver" to get started'}
                        </div>
                    </div>
                ) : isMobile ? (
                    <div>
                        {filteredDrivers.map(renderMobileCard)}
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Name</th>
                                <th style={styles.th}>Number</th>
                                <th style={styles.th}>Email</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Car</th>
                                <th style={styles.th}>Days</th>
                                <th style={styles.th}>Links</th>
                                <th style={styles.th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDrivers.map((driver) => (
                                <tr key={driver.id}>
                                    <td style={styles.td}>{driver.first_name} {driver.last_name}</td>
                                    <td style={styles.td}>{driver.phone_number}</td>
                                    <td style={styles.td}>{driver.email}</td>
                                    <td style={styles.td}>{driver.status}</td>
                                    <td style={styles.td}>{driver.car_color}</td>
                                    <td style={styles.td}>{driver.days_done}</td>
                                    <td style={styles.td}>
                                        <a href={`/home/driver_graph?driver=${driver.id}`} style={styles.link}>Graph</a>
                                        <a href={`/sales/driver_specific_sales?driver=${driver.id}`} style={styles.link}>Sales</a>
                                        <a href={`/sales/driver_specific_expenses?driver=${driver.id}`} style={styles.link}>Expenses</a>
                                        <a href={`/sales/driver_specific_sales_this_month?driver=${driver.id}`} style={styles.link}>This Month</a>
                                    </td>
                                    <td style={styles.td}>
                                        <button
                                            onClick={() => {
                                                setEditingDriver(driver);
                                                setShowModal(true);
                                            }}
                                            style={{ ...styles.actionButton, ...styles.editButton }}
                                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirm(driver)}
                                            style={{ ...styles.actionButton, ...styles.deleteButton }}
                                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Driver Create/Edit Modal */}
            <DriverModal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setEditingDriver(null);
                }}
                driver={editingDriver}
                onSave={handleSave}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmModal
                isOpen={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                driver={deleteConfirm}
                onConfirm={handleDelete}
            />
        </div>
    );
};

export default Drivers;
